import React, { ChangeEvent, ReactNode, useCallback, useMemo, useState } from 'react';
import {
  SearchContainerStyled,
  VariableItemListStyled,
  VariableItemListSubHeaderStyled,
  VariableListStyled,
  VariablesCategoryLabelStyled,
  VariablesPickerContainerStyled,
  VariablesPickerContentStyled,
  VariablesPickerDropdownListStyled,
  VariablesPickerEmptyStateIconStyled,
  VariablesPickerEmptyStateStyled,
  VariablesPickerEmptyStateTitleStyled,
  VariablesPickerStyled
} from './VariablesPicker.styled';
import Input from '../Input/Input';
import { Icon } from '../Icon';
import { useTheme } from '@mui/material/styles';
import { alpha, Box, BoxProps, ClickAwayListener, Typography } from '@mui/material';
import Chip from '@mui/material/Chip';
import { tooltipClasses } from '@mui/material/Tooltip';
import { TooltipProps } from '@mui/material/Tooltip/Tooltip';
import { nanoid } from 'nanoid';

export interface IOption {
  id: string;
  value: string;
  label: string;
}

interface ICategory {
  id: string;
  label: string;
  icon?: ReactNode;
  color?: string;
  values: { subheader?: string; options: IOption[] }[];
}

export interface IVariablePickerProps {
  id?: string;
  isOpen?: boolean;
  autoFocus?: boolean;
  dataTestId?: string;
  onClose?: () => void;
  placeHolder?: string;
  categories?: ICategory[];
  onSelectVariable?: (value: string) => void;
  onClickAway?: (event: MouseEvent | TouchEvent) => void;
  placement?: TooltipProps['placement'];
  width?: string;
  wrapperComponentProps?: BoxProps;
  /**
   * The VariablesPicker will be added relative to this element.
   */
  children?: ReactNode | undefined;
}

export const VariablesPicker = React.forwardRef<HTMLDivElement, IVariablePickerProps>(
  (
    {
      id = 'variable-picker',
      isOpen = false,
      autoFocus = true,
      onClose,
      placeHolder = 'Search',
      dataTestId = 'variable-field',
      categories = [],
      onSelectVariable,
      onClickAway = () => undefined,
      placement,
      width,
      wrapperComponentProps,
      children
    },
    ref
  ) => {
    const theme = useTheme();
    const [search, setSearch] = useState<string>('');

    const filteredCategories = useMemo(() => {
      return categories.reduce((acc, category) => {
        let optionsWithSearchValue: IOption[] = [];
        const categoriesWithSearchValue = category.values.filter(item => {
          optionsWithSearchValue = item.options.filter(({ label }) =>
            label.toLowerCase().includes(search.toLowerCase())
          );
          return optionsWithSearchValue.length;
        });

        if (categoriesWithSearchValue.length > 0) {
          acc.push({
            ...category,
            values: categoriesWithSearchValue.map(item => ({ ...item, options: optionsWithSearchValue }))
          });
        }

        return acc;
      }, [] as ICategory[]);
    }, [search, categories]);

    const handleChangeSearch = useCallback(
      (event: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
        setSearch(event.currentTarget.value);
      },
      [setSearch]
    );

    if (!children) {
      throw new Error('VariablesPicker must have child element');
    }

    const [pickerKey, updatePickerKey] = useState(nanoid());

    return (
      <VariablesPickerStyled
        id={id}
        open={isOpen}
        onClose={onClose}
        data-testid={dataTestId}
        arrow
        placement={placement}
        onOpen={() => {
          setSearch('');
        }}
        componentsProps={{
          tooltip: { sx: { padding: 0, fontWeight: theme.typography.fontWeightLight, maxWidth: width } }
        }}
        PopperProps={{
          sx: {
            padding: theme.spacing(1),
            width,

            [`& .${tooltipClasses.arrow}`]: {
              fontSize: '18px',
              '&:before': {
                border: `1px solid ${theme.palette.primary.main}`,
                background: theme.palette.grey[600]
              }
            }
          }
        }}
        title={
          <ClickAwayListener onClickAway={onClickAway} mouseEvent="onMouseDown">
            <VariablesPickerContentStyled>
              <SearchContainerStyled>
                <Input
                  fullWidth
                  autoFocus={autoFocus}
                  type={'text'}
                  placeholder={placeHolder}
                  value={search}
                  onChange={handleChangeSearch}
                  dataTestId="autocomplete-input"
                  startAdornment={<Icon name="search" />}
                />
              </SearchContainerStyled>

              {filteredCategories.length ? (
                <VariablesPickerContainerStyled>
                  {filteredCategories.map(({ id, label, icon, color, values = [] }) => (
                    <VariablesPickerDropdownListStyled
                      key={`${id}-${search}`}
                      defaultExpanded
                      onChange={() => {
                        updatePickerKey(nanoid());
                      }}
                    >
                      <VariablesCategoryLabelStyled>
                        {icon}
                        <Typography fontSize={'13px'} fontWeight={'bold'} lineHeight={'22px'} color="grey.100" noWrap>
                          {label}
                        </Typography>
                      </VariablesCategoryLabelStyled>

                      <VariableListStyled>
                        {values.map(({ subheader, options }, index) => (
                          <VariableItemListStyled key={index}>
                            {subheader && (
                              <VariableItemListSubHeaderStyled>
                                <Typography variant="body2" fontSize={'13px'} color={'grey.200'} noWrap>
                                  {subheader}
                                </Typography>
                              </VariableItemListSubHeaderStyled>
                            )}

                            {options.map(({ id: optionId, label: optionLabel, value }) => (
                              <Chip
                                key={`${optionId}-${pickerKey}`}
                                size={'small'}
                                label={optionLabel}
                                onClick={() => {
                                  if (!onSelectVariable) {
                                    return;
                                  }
                                  onSelectVariable(value);
                                }}
                                sx={{
                                  backgroundColor: color ? alpha(color, 0.24) : undefined,
                                  marginBottom: '7px',
                                  marginTop: '1px',
                                  color: theme.palette.common.white,

                                  '&:hover': {
                                    backgroundColor: color ? alpha(color, 0.24) : undefined,
                                    outline: color ? `1px solid ${color}` : undefined
                                  }
                                }}
                              />
                            ))}
                          </VariableItemListStyled>
                        ))}
                      </VariableListStyled>
                    </VariablesPickerDropdownListStyled>
                  ))}
                </VariablesPickerContainerStyled>
              ) : (
                <VariablesPickerEmptyStateStyled>
                  <VariablesPickerEmptyStateIconStyled>
                    <Icon name="search" color={'white'} width={34} height={34} />
                  </VariablesPickerEmptyStateIconStyled>

                  <VariablesPickerEmptyStateTitleStyled>
                    <Typography variant="h5" fontSize={'20px'} color={'white'} noWrap>
                      No results found
                    </Typography>
                    <Typography variant="body2" fontSize={'14px'} color={'grey.200'} noWrap>
                      Try adjusting your search
                    </Typography>
                  </VariablesPickerEmptyStateTitleStyled>
                </VariablesPickerEmptyStateStyled>
              )}
            </VariablesPickerContentStyled>
          </ClickAwayListener>
        }
      >
        <Box ref={ref} component={'span'} {...wrapperComponentProps}>
          {children}
        </Box>
      </VariablesPickerStyled>
    );
  }
);

VariablesPicker.displayName = 'VariablesPicker';
